import PressableCustom from '@src/components/general/PressableCustom/PressableCustom';
import useViewPort, {
  useGetAbsoluteLeft,
} from 'design-system/src/Hooks/useViewPort';
import Icon from 'design-system/src/Components/Icon/Icon';
import Typography from 'design-system/src/Components/Typography/Typography';
import {typographyVariantStyled} from 'design-system/src/Components/Typography/Typography.styles';
import useBreakpoint from 'design-system/src/WebComponent/Grid/hooks/useBreakpoint';
import React, {FC, useEffect, useRef, useState} from 'react';

interface IAppDropDownMenuModalProps<T extends object = object> {
  title?: string;
  items?: IAppDropDownItem<T>[];
  titleVariant?: keyof typeof typographyVariantStyled;
  containerWidth?: number | string;
  onClickItem?(item: T): void;
  style?: React.CSSProperties;
}

export type IAppDropDownItem<T extends object = object> = T & {
  title?: string;
  onPress?(): void;
};

const AppDropDownMenuModal = <T extends object = object>({
  title,
  items,
  titleVariant = 'h2',
  style,
  onClickItem,
  containerWidth,
}: IAppDropDownMenuModalProps<T>) => {
  const {isMobile} = useBreakpoint();
  const [isOpen, setIsOpen] = useState(false);
  const [hover, setHover] = useState(false);

  const dropdownMenuRef = useRef(null);
  const triggerRef = useRef(null);

  const {viewportHeight} = useViewPort();
  const {leftPosition} = useGetAbsoluteLeft(triggerRef, [isOpen], {
    type: 'offset',
  });

  const handleItemClick = (element: IAppDropDownItem<T>) => () => {
    if (element.onPress) {
      element.onPress();
    } else {
      onClickItem?.(element);
    }
    setIsOpen(false);
  };

  return (
    <div
      ref={triggerRef}
      className="AppDropDownMenuModal"
      style={{
        position: 'relative',
        ...style,
      }}>
      <div
        style={{
          display: 'inline-flex',
          gap: 8,
          alignItems: 'center',
          width: 'fit-content',
          cursor: 'pointer',
        }}
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        onMouseEnter={() => {
          setHover(true);
        }}
        onMouseLeave={() => setHover(false)}>
        <Typography
          variant={titleVariant}
          color={hover || isOpen ? 'primary' : undefined}>
          {title}
        </Typography>
        <Icon
          name="caret-down"
          color={hover || isOpen ? '#EA6C13' : undefined}
        />
      </div>
      {isOpen && (
        <div
          ref={dropdownMenuRef}
          style={{
            position: isMobile ? 'fixed' : 'absolute',
            width: containerWidth ?? '100vw',
            top: isMobile ? 0 : 40,
            left: isMobile ? 0 : leftPosition,
            zIndex: 1000,
            padding: isMobile ? 0 : '0 60px',
            boxSizing: 'border-box',
          }}>
          <div
            style={{
              width: '100%',
              height: isMobile ? viewportHeight : 192,
              backgroundColor: 'rgba(27, 28, 33, 0.50)',
              backdropFilter: `blur(${isMobile ? 30 : 20}px)`,
              padding: isMobile ? '60px 0' : 37,
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'center' : undefined,
              justifyContent: isMobile ? 'space-between' : undefined,
            }}>
            {isMobile && <Typography variant="htitle">{title}</Typography>}
            {isMobile && (
              <style
                dangerouslySetInnerHTML={{
                  __html: `body {overflow: hidden;}`,
                }}></style>
            )}
            <div
              style={{
                display: 'inline-flex',
                flexDirection: 'column',
                flexWrap: 'wrap',
                gap: 11,
                width: isMobile ? undefined : '20%',
                alignItems: isMobile ? 'center' : undefined,
              }}>
              {items?.map((item, index) => (
                <PressableCustom
                  key={`menu-${index}`}
                  onPress={handleItemClick(item)}>
                  {({hover}) => (
                    <Typography
                      variant="body2"
                      color={hover ? 'primary' : undefined}>
                      {item?.title}
                    </Typography>
                  )}
                </PressableCustom>
              ))}
            </div>
            {isMobile && (
              <div
                onClick={() => setIsOpen(!isOpen)}
                style={{
                  height: 64,
                  width: 64,
                  borderRadius: 32,
                  backgroundColor: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                }}>
                <Icon name="close" size="76" color="black" />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AppDropDownMenuModal;
